import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { formatRelative } from "date-fns";
import TreatmentFocusArea from "../TreatmentFocusArea";
import TodaysClinicalTargets from "./TodaysClinicalTargets";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import errorStateImg from "../../../../assets/error-state-clinical-program.png";
import useGetClinicalProgramTargets from "hooks/api/client/useGetClinicalProgramTargets";
import useSaveClinicalProgramTargets from "hooks/api/client/useSaveClinicalProgramTargets";
import { useGetClientById } from "hooks/api/useGetClientById";
import { useClientVideoCalls } from "hooks/api/client/useClientVideoCalls";
import type { ClinicalTarget } from "@answersnow/pkg-clinical-program";

const uniqueTargetsById = (targets: ClinicalTarget[]): ClinicalTarget[] => {
  const seen = new Set<string>();
  const out: ClinicalTarget[] = [];
  for (const target of targets) {
    if (seen.has(target.id)) continue;
    seen.add(target.id);
    out.push(target);
  }
  return out;
};

const ClinicalProgram: React.FC = () => {
  const { id: clientIdStr } = useParams<{ id: string }>();
  const clientId = clientIdStr ? Number(clientIdStr) : null;
  const history = useHistory();

  const { data: clientData } = useGetClientById(clientId);
  const clientFirstName: string = clientData?.data?.first_name ?? "";

  const {
    selectedTargets,
    availableTargets,
    hasNoTargets,
    hasIneligibleTargets,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useGetClinicalProgramTargets(clientId);

  const { saveClinicalProgramTargets, resetClinicalProgramTargets, isSaving, isResetting } =
    useSaveClinicalProgramTargets();

  const { videoCalls, videoCallsLoading } = useClientVideoCalls(clientId ?? 0, "upcoming", {
    size: 1,
  });

  const nextSessionLabel = useMemo(() => {
    const next = videoCalls?.[0];
    if (!next) return undefined;
    const relative = formatRelative(new Date(next.scheduled_date), new Date());
    return `Next Session: ${relative.charAt(0).toUpperCase()}${relative.slice(1)}`;
  }, [videoCalls]);

  // Local edit state mirrors the fetched data until Save
  const [localSelected, setLocalSelected] = useState<ClinicalTarget[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    setInitialized(false);
  }, [clientId]);

  // Sync local state once data loads (only on first load or after reset)
  useEffect(() => {
    if (!isFetching && !isLoading && !isError && !initialized) {
      setLocalSelected(uniqueTargetsById(selectedTargets));
      setInitialized(true);
    }
  }, [isFetching, isLoading, isError, initialized, selectedTargets]);

  const handleSave = useCallback(async () => {
    if (!clientId) return;
    const targetIds = [...new Set(localSelected.map((t) => Number(t.id)))];
    await saveClinicalProgramTargets({
      clientId,
      targetIds,
    });
    await refetch();
    setInitialized(false);
  }, [clientId, localSelected, saveClinicalProgramTargets, refetch]);

  const handleReset = useCallback(async () => {
    if (!clientId) return;
    await resetClinicalProgramTargets({ clientId });
    await refetch();
    setInitialized(false);
  }, [clientId, resetClinicalProgramTargets, refetch]);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleGoToGoalsAndTargets = useCallback(() => {
    if (clientId) history.push(`/clients/${clientId}/goals-targets`);
  }, [clientId, history]);

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          minHeight: 480,
          gap: 1,
          p: 1,
          borderRadius: "12px",
          bgcolor: "background.default",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: 410,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            component="img"
            src={errorStateImg}
            alt=""
            sx={{ width: 200, height: 200, objectFit: "cover", pointerEvents: "none" }}
          />
          <Box sx={{ textAlign: "center", width: "100%" }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 500,
                lineHeight: 1.75,
                letterSpacing: "0.15px",
                color: "text.primary",
              }}
            >
              {"We couldn't load the program."}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: 1.43,
                letterSpacing: "0.17px",
                color: "text.secondary",
              }}
            >
              Something went wrong on our end. Your saved targets and plan are safe — try refreshing
              the page.
            </Typography>
          </Box>
          <Button variant="contained" color="secondary" size="small" onClick={handleRetry}>
            Try Again
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Stack spacing={6} sx={{ width: "100%" }}>
      {isLoading ? (
        <Skeleton variant="rounded" width="100%" height={280} sx={{ borderRadius: "16px" }} />
      ) : (
        <TreatmentFocusArea />
      )}
      <TodaysClinicalTargets
        selectedTargets={initialized ? localSelected : selectedTargets}
        availableTargets={availableTargets}
        serverSelectedTargets={selectedTargets}
        hasNoTargets={hasNoTargets}
        hasIneligibleTargets={hasIneligibleTargets}
        clientFirstName={clientFirstName}
        nextSessionLabel={nextSessionLabel}
        isLoading={isLoading || isFetching || isSaving || isResetting || videoCallsLoading}
        onSave={handleSave}
        onReset={handleReset}
        onTargetAdd={(targetId) => {
          const target = [...selectedTargets, ...availableTargets].find((t) => t.id === targetId);
          if (target) {
            setLocalSelected((prev) => {
              if (prev.some((t) => t.id === targetId)) return prev;
              return [...prev, target];
            });
          }
        }}
        onTargetRemove={(targetId) => {
          setLocalSelected((prev) => prev.filter((t) => t.id !== targetId));
        }}
        onTargetReorder={(targetId, direction) => {
          setLocalSelected((prev) => {
            const index = prev.findIndex((t) => t.id === targetId);
            if (index === -1) return prev;
            const updated = [...prev];
            if (direction === "up" && index > 0) {
              [updated[index], updated[index - 1]] = [updated[index - 1], updated[index]];
            } else if (direction === "down" && index < updated.length - 1) {
              [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
            }
            return updated;
          });
        }}
        onTargetReorderByIndex={(fromIndex, toIndex) => {
          setLocalSelected((prev) => {
            const updated = [...prev];
            const [moved] = updated.splice(fromIndex, 1);
            updated.splice(toIndex, 0, moved);
            return updated;
          });
        }}
        onGoToGoalsAndTargets={handleGoToGoalsAndTargets}
      />
    </Stack>
  );
};

export default ClinicalProgram;
